import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, UserCircle, Activity, Shield, Bell,
  CreditCard, FileText, Search, ChevronDown, ChevronRight,
  LogOut, Settings, TrendingUp, TrendingDown, Eye, Edit, Ban,
  CheckCircle, XCircle, Download, Send, Filter, RefreshCw,
  BarChart3, ArrowUpRight, ArrowDownRight, Clock, Heart,
  Stethoscope, Building2, FlaskConical, Phone, Mail, MapPin,
  Calendar, DollarSign, Loader2, ChevronLeft, MoreHorizontal,
  AlertTriangle, Lock, Zap, Globe, Tag, UserCheck, UserX, Menu, X
} from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const API_URL = (() => {
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (!h.includes('localhost') && !h.includes('127.0.0.1'))
      return '';
  }
  return process.env.REACT_APP_BACKEND_URL || '';
})();

const CHART_COLORS = ['#F97316', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899'];

// ==================== KPI CARD ====================
const KpiCard = ({ label, value, icon: Icon, color, trend, trendLabel }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      {trend !== undefined && (
        <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${trend >= 0 ? 'text-emerald-700 bg-emerald-50' : 'text-red-700 bg-red-50'}`}>
          {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {Math.abs(trend)}%
        </span>
      )}
    </div>
    <div className="text-2xl font-bold text-gray-900 tabular-nums">{typeof value === 'number' ? value.toLocaleString() : value}</div>
    <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mt-1">{label}</div>
    {trendLabel && <div className="text-[10px] text-gray-400 mt-0.5">{trendLabel}</div>}
  </div>
);

// ==================== USER ROW ====================
const UserRow = ({ user, onView, onEdit, selected, onSelect }) => (
  <tr className="border-b border-gray-50 hover:bg-orange-50/30 transition-colors">
    <td className="py-3 px-4">
      <input type="checkbox" checked={selected} onChange={() => onSelect(user.id)} className="rounded border-gray-300 text-orange-500 focus:ring-orange-400" />
    </td>
    <td className="py-3 px-4">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-amber-400 flex items-center justify-center text-white font-bold text-sm">
          {(user.name || 'U')[0].toUpperCase()}
        </div>
        <div>
          <div className="font-semibold text-gray-900 text-sm">{user.name || 'Unknown'}</div>
          <div className="text-xs text-gray-500">{user.email}</div>
        </div>
      </div>
    </td>
    <td className="py-3 px-4">
      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full ${
        user.role === 'admin' ? 'bg-purple-100 text-purple-700' :
        user.role === 'doctor' ? 'bg-blue-100 text-blue-700' :
        user.role === 'clinic' ? 'bg-emerald-100 text-emerald-700' :
        user.role === 'lab' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'
      }`}>{user.role || 'user'}</span>
    </td>
    <td className="py-3 px-4 text-sm text-gray-600">{user.phone_number || '-'}</td>
    <td className="py-3 px-4 text-sm text-gray-600">{[user.city, user.state].filter(Boolean).join(', ') || '-'}</td>
    <td className="py-3 px-4">
      <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${user.plan === 'pro' ? 'bg-orange-100 text-orange-700' : user.plan === 'basic' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
        {user.plan || 'Free'}
      </span>
    </td>
    <td className="py-3 px-4 text-xs text-gray-500">
      {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
    </td>
    <td className="py-3 px-4">
      <div className="flex items-center gap-1">
        <button onClick={() => onView(user)} className="p-1.5 hover:bg-gray-100 rounded-lg transition" title="View"><Eye className="w-4 h-4 text-gray-500" /></button>
        <button onClick={() => onEdit(user)} className="p-1.5 hover:bg-gray-100 rounded-lg transition" title="Edit"><Edit className="w-4 h-4 text-gray-500" /></button>
      </div>
    </td>
  </tr>
);

// ==================== MAIN ADMIN PANEL ====================
const InfuseAdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuth, setIsAuth] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [adminInfo, setAdminInfo] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [activeModule, setActiveModule] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dateRange, setDateRange] = useState('30d');

  // Dashboard data
  const [kpis, setKpis] = useState(null);
  // Users data
  const [users, setUsers] = useState([]);
  const [userTotal, setUserTotal] = useState(0);
  const [userPage, setUserPage] = useState(1);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  // User 360
  const [selectedUser360, setSelectedUser360] = useState(null);
  const [user360Data, setUser360Data] = useState(null);
  const [user360Tab, setUser360Tab] = useState('profile');
  // Audit logs
  const [auditLogs, setAuditLogs] = useState([]);
  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [notifForm, setNotifForm] = useState({ title: '', message: '', target_segment: 'all' });
  // Revenue
  const [revenue, setRevenue] = useState(null);
  // Internal admins (super admin only)
  const [internalAdmins, setInternalAdmins] = useState([]);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [adminForm, setAdminForm] = useState({ email: '', name: '', admin_function: 'support_admin' });
  const [newAdminCredentials, setNewAdminCredentials] = useState(null);
  // Tenant team (clinic/lab/hospital admins)
  const [teamMembers, setTeamMembers] = useState([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [memberForm, setMemberForm] = useState({ email: '', name: '', role: 'doctor', phone: '' });
  const [newMemberCredentials, setNewMemberCredentials] = useState(null);

  const headers = { 'Content-Type': 'application/json', 'x-admin-key': adminKey, 'x-admin-email': adminInfo?.email || '' };

  // ---- AUTH ----
  useEffect(() => {
    const saved = sessionStorage.getItem('platform_admin_session');
    if (saved) {
      try {
        const s = JSON.parse(saved);
        if (s.key && s.admin) { setIsAuth(true); setAdminKey(s.key); setAdminInfo(s.admin); }
      } catch {}
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (res.ok && data.key) {
        setAdminKey(data.key);
        setAdminInfo(data.admin);
        setIsAuth(true);
        sessionStorage.setItem('platform_admin_session', JSON.stringify({ key: data.key, admin: data.admin }));
      } else {
        setLoginError(data.detail || 'Login failed');
      }
    } catch { setLoginError('Network error. Please try again.'); }
  };

  const handleLogout = () => {
    setIsAuth(false);
    setAdminKey('');
    setAdminInfo(null);
    sessionStorage.removeItem('platform_admin_session');
  };

  // Tenant context helper
  const tenantParams = adminInfo?.tenant_type && adminInfo.tenant_type !== 'infuse'
    ? `&tenant_type=${adminInfo.tenant_type}&tenant_id=${adminInfo.tenant_id}`
    : '';

  // ---- DATA FETCHERS ----
  const fetchKpis = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/dashboard/kpis?_=1${tenantParams}`, { headers });
      if (res.ok) setKpis(await res.json());
    } catch {}
  }, [adminKey, adminInfo]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: userPage, limit: 20, search: userSearch, role: userRoleFilter });
      const res = await fetch(`${API_URL}/api/platform-admin/users?${params}${tenantParams}`, { headers });
      if (res.ok) { const d = await res.json(); setUsers(d.users); setUserTotal(d.total); }
    } catch {}
    setLoading(false);
  }, [adminKey, userPage, userSearch, userRoleFilter, adminInfo]);

  const fetchUser360 = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/users/${userId}?_=1${tenantParams}`, { headers });
      if (res.ok) setUser360Data(await res.json());
    } catch {}
  };

  // Audit log filter state
  const [auditFilters, setAuditFilters] = useState({ action: '', admin_email: '', start_date: '', end_date: '' });
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditBreakdown, setAuditBreakdown] = useState({});
  // Revenue range
  const [revenueRange, setRevenueRange] = useState(180);

  const fetchAuditLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        ...(auditFilters.action ? { action: auditFilters.action } : {}),
        ...(auditFilters.admin_email ? { admin_email: auditFilters.admin_email } : {}),
        ...(auditFilters.start_date ? { start_date: auditFilters.start_date } : {}),
        ...(auditFilters.end_date ? { end_date: auditFilters.end_date } : {}),
      });
      const url = `${API_URL}/api/platform-admin/audit-logs?${params}${tenantParams.replace('?', '&')}`;
      const res = await fetch(url, { headers });
      if (res.ok) {
        const d = await res.json();
        setAuditLogs(d.logs || []);
        setAuditTotal(d.total || 0);
        setAuditBreakdown(d.action_breakdown || {});
      }
    } catch {}
  }, [adminKey, adminInfo, auditFilters]);

  const exportAuditLogs = async () => {
    try {
      const params = new URLSearchParams({
        ...(auditFilters.action ? { action: auditFilters.action } : {}),
        ...(auditFilters.admin_email ? { admin_email: auditFilters.admin_email } : {}),
        ...(auditFilters.start_date ? { start_date: auditFilters.start_date } : {}),
        ...(auditFilters.end_date ? { end_date: auditFilters.end_date } : {}),
      });
      const url = `${API_URL}/api/platform-admin/audit-logs/export?${params}${tenantParams.replace('?', '&')}`;
      const res = await fetch(url, { headers });
      if (!res.ok) { alert('Export failed'); return; }
      const blob = await res.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `audit_logs_${Date.now()}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch { alert('Network error'); }
  };

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/notifications?_=1${tenantParams}`, { headers });
      if (res.ok) { const d = await res.json(); setNotifications(d.notifications); }
    } catch {}
  }, [adminKey, adminInfo]);

  const fetchRevenue = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/revenue/analytics?range_days=${revenueRange}${tenantParams.replace('?', '&')}`, { headers });
      if (res.ok) setRevenue(await res.json());
    } catch {}
  }, [adminKey, adminInfo, revenueRange]);

  const fetchInternalAdmins = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/internal-admins`, { headers });
      if (res.ok) { const d = await res.json(); setInternalAdmins(d.admins || []); }
    } catch {}
  }, [adminKey, adminInfo]);

  const fetchTeamMembers = useCallback(async () => {
    if (!adminInfo?.tenant_type || adminInfo.tenant_type === 'infuse') return;
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/tenant/members?tenant_type=${adminInfo.tenant_type}&tenant_id=${adminInfo.tenant_id}`, { headers });
      if (res.ok) { const d = await res.json(); setTeamMembers(d.members || []); }
    } catch {}
  }, [adminKey, adminInfo]);

  const handleCreateInternalAdmin = async () => {
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/internal-admins`, {
        method: 'POST', headers, body: JSON.stringify(adminForm)
      });
      const data = await res.json();
      if (res.ok) {
        setNewAdminCredentials(data);
        setAdminForm({ email: '', name: '', admin_function: 'support_admin' });
        fetchInternalAdmins();
      } else {
        alert(data.detail || 'Failed to create admin');
      }
    } catch (e) { alert('Network error'); }
  };

  const handleUpdateInternalAdmin = async (adminId, updates) => {
    try {
      await fetch(`${API_URL}/api/platform-admin/internal-admins/${adminId}`, {
        method: 'PATCH', headers, body: JSON.stringify(updates)
      });
      fetchInternalAdmins();
    } catch {}
  };

  const handleResetInternalAdminPwd = async (adminId) => {
    const newPwd = prompt('Enter new password (min 6 chars):');
    if (!newPwd || newPwd.length < 6) return;
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/internal-admins/${adminId}/reset-password`, {
        method: 'POST', headers, body: JSON.stringify({ new_password: newPwd })
      });
      if (res.ok) alert('Password reset successfully');
      else alert('Failed to reset password');
    } catch {}
  };

  const handleDeleteInternalAdmin = async (adminId, email) => {
    if (!window.confirm(`Delete admin ${email}? This cannot be undone.`)) return;
    try {
      await fetch(`${API_URL}/api/platform-admin/internal-admins/${adminId}`, { method: 'DELETE', headers });
      fetchInternalAdmins();
    } catch {}
  };

  const handleAddTeamMember = async () => {
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/tenant/members?tenant_type=${adminInfo.tenant_type}&tenant_id=${adminInfo.tenant_id}`, {
        method: 'POST', headers, body: JSON.stringify(memberForm)
      });
      const data = await res.json();
      if (res.ok) {
        setNewMemberCredentials(data);
        setMemberForm({ email: '', name: '', role: 'doctor', phone: '' });
        fetchTeamMembers();
      } else {
        alert(data.detail || 'Failed to add member');
      }
    } catch { alert('Network error'); }
  };

  const handleUpdateTeamMember = async (userId, updates) => {
    try {
      await fetch(`${API_URL}/api/platform-admin/tenant/members/${userId}?tenant_type=${adminInfo.tenant_type}&tenant_id=${adminInfo.tenant_id}`, {
        method: 'PATCH', headers, body: JSON.stringify(updates)
      });
      fetchTeamMembers();
    } catch {}
  };

  const handleRemoveTeamMember = async (userId, email) => {
    if (!window.confirm(`Remove ${email} from your organization?`)) return;
    try {
      await fetch(`${API_URL}/api/platform-admin/tenant/members/${userId}?tenant_type=${adminInfo.tenant_type}&tenant_id=${adminInfo.tenant_id}`, {
        method: 'DELETE', headers
      });
      fetchTeamMembers();
    } catch {}
  };

  const handleResetTeamMemberPwd = async (userId) => {
    const newPwd = prompt('Enter new password for this member (min 6 chars):');
    if (!newPwd || newPwd.length < 6) return;
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/tenant/members/${userId}/reset-password?tenant_type=${adminInfo.tenant_type}&tenant_id=${adminInfo.tenant_id}`, {
        method: 'POST', headers, body: JSON.stringify({ new_password: newPwd })
      });
      if (res.ok) alert('Password reset successfully');
    } catch {}
  };

  useEffect(() => {
    if (!isAuth) return;
    if (activeModule === 'dashboard') fetchKpis();
    if (activeModule === 'users' || activeModule === 'healthtrack-users') fetchUsers();
    if (activeModule === 'audit') fetchAuditLogs();
    if (activeModule === 'notifications') fetchNotifications();
    if (activeModule === 'revenue') fetchRevenue();
    if (activeModule === 'internal-admins') fetchInternalAdmins();
    if (activeModule === 'team') fetchTeamMembers();
  }, [isAuth, activeModule, fetchKpis, fetchUsers, fetchAuditLogs, fetchNotifications, fetchRevenue, fetchInternalAdmins, fetchTeamMembers]);

  const handleViewUser = (user) => {
    setSelectedUser360(user);
    setUser360Tab('profile');
    fetchUser360(user.id);
    setActiveModule('user360');
  };

  const handleSendNotification = async () => {
    try {
      await fetch(`${API_URL}/api/platform-admin/notifications/send?_=1${tenantParams}`, {
        method: 'POST', headers, body: JSON.stringify(notifForm)
      });
      setNotifForm({ title: '', message: '', target_segment: 'all' });
      fetchNotifications();
    } catch {}
  };

  const handleBulkAction = async (action, value) => {
    if (selectedUsers.length === 0) return;
    try {
      await fetch(`${API_URL}/api/platform-admin/users/bulk-action`, {
        method: 'POST', headers, body: JSON.stringify({ user_ids: selectedUsers, action, value })
      });
      setSelectedUsers([]);
      fetchUsers();
    } catch {}
  };

  const toggleSelectUser = (id) => {
    setSelectedUsers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // ==================== LOGIN SCREEN ====================
  if (!isAuth) return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">Infuse Admin</h1>
          <p className="text-gray-400 mt-1">Platform Administration Portal</p>
        </div>
        <form onSubmit={handleLogin} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 space-y-4">
          {loginError && <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">{loginError}</div>}
          <div>
            <label className="text-sm font-medium text-gray-300 mb-1 block">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@infuse.net.in"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 outline-none" required />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-300 mb-1 block">Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 outline-none" required />
          </div>
          <button type="submit" className="w-full py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-amber-600 transition shadow-lg">
            Sign In
          </button>
        </form>
      </div>
    </div>
  );

  // ==================== SIDEBAR ====================
  const isSuperAdmin = adminInfo?.admin_function === 'super_admin' || adminInfo?.role === 'super_admin';
  const isInfuseInternal = adminInfo?.tenant_type === 'infuse';
  const isTenantAdmin = adminInfo?.tenant_type && ['clinic', 'lab', 'hospital'].includes(adminInfo.tenant_type);

  const MODULES = [
    { id: 'dashboard', label: 'Overview', icon: LayoutDashboard, show: true },
    { id: 'users', label: 'All Users', icon: Users, show: isInfuseInternal },
    { id: 'healthtrack-users', label: 'HealthTrack Users', icon: Heart, show: isInfuseInternal },
    { id: 'team', label: 'My Team', icon: UserCheck, show: isTenantAdmin },
    { id: 'revenue', label: 'Revenue Analytics', icon: DollarSign, show: true },
    { id: 'notifications', label: 'Notifications', icon: Bell, show: true },
    { id: 'audit', label: 'Audit Logs', icon: FileText, show: true },
    { id: 'plans', label: 'Plans & Pricing', icon: CreditCard, show: isInfuseInternal },
    { id: 'internal-admins', label: 'Internal Admins', icon: Shield, show: isSuperAdmin },
    { id: 'rbac', label: 'Roles & Access', icon: Lock, show: isInfuseInternal },
  ].filter(m => m.show);

  // ==================== RENDER MODULES ====================
  const renderDashboard = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Overview</h1>
          <p className="text-sm text-gray-500">Real-time metrics across all products</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={dateRange} onChange={e => setDateRange(e.target.value)}
            className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700">
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="365d">Last Year</option>
          </select>
          <button onClick={fetchKpis} className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>
      {!kpis ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <KpiCard label="Total Users" value={kpis.total_users} icon={Users} color="bg-gradient-to-br from-orange-500 to-amber-500" trend={12} trendLabel="vs last month" />
            <KpiCard label="Active Users" value={kpis.active_users} icon={Activity} color="bg-gradient-to-br from-emerald-500 to-teal-500" trend={8} />
            <KpiCard label="New Signups (30d)" value={kpis.new_signups_30d} icon={UserCheck} color="bg-gradient-to-br from-blue-500 to-cyan-500" trend={15} />
            <KpiCard label="Paid Users" value={kpis.paid_users} icon={CreditCard} color="bg-gradient-to-br from-purple-500 to-violet-500" />
            <KpiCard label="Free Users" value={kpis.free_users} icon={UserCircle} color="bg-gradient-to-br from-gray-500 to-slate-500" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard label="Revenue" value={`$${kpis.total_revenue.toLocaleString()}`} icon={DollarSign} color="bg-gradient-to-br from-green-500 to-emerald-500" trend={22} />
            <KpiCard label="ARPU" value={`$${kpis.arpu}`} icon={TrendingUp} color="bg-gradient-to-br from-amber-500 to-yellow-500" />
            <KpiCard label="Churn Rate" value={`${kpis.churn_rate}%`} icon={TrendingDown} color="bg-gradient-to-br from-red-500 to-rose-500" trend={-0.5} />
            <KpiCard label="System Uptime" value={`${kpis.system_uptime}%`} icon={Zap} color="bg-gradient-to-br from-green-500 to-lime-500" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard label="Users" value={kpis.role_breakdown.users} icon={UserCircle} color="bg-orange-500" />
            <KpiCard label="Doctors" value={kpis.role_breakdown.doctors} icon={Stethoscope} color="bg-blue-500" />
            <KpiCard label="Clinics" value={kpis.role_breakdown.clinics} icon={Building2} color="bg-emerald-500" />
            <KpiCard label="Labs" value={kpis.role_breakdown.labs} icon={FlaskConical} color="bg-amber-500" />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-bold text-gray-900 mb-4">User Growth (Last 6 Months)</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={[
                  { month: 'Nov', users: Math.max(10, kpis.total_users - 80) },
                  { month: 'Dec', users: Math.max(20, kpis.total_users - 60) },
                  { month: 'Jan', users: Math.max(35, kpis.total_users - 45) },
                  { month: 'Feb', users: Math.max(50, kpis.total_users - 30) },
                  { month: 'Mar', users: Math.max(70, kpis.total_users - 15) },
                  { month: 'Apr', users: kpis.total_users },
                ]}>
                  <defs>
                    <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F97316" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#F97316" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9CA3AF' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#9CA3AF' }} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #f3f4f6', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.06)' }} />
                  <Area type="monotone" dataKey="users" stroke="#F97316" strokeWidth={2.5} fill="url(#colorUsers)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-bold text-gray-900 mb-4">Role Distribution</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={[
                    { name: 'Users', value: kpis.role_breakdown.users },
                    { name: 'Doctors', value: kpis.role_breakdown.doctors },
                    { name: 'Clinics', value: kpis.role_breakdown.clinics },
                    { name: 'Labs', value: kpis.role_breakdown.labs },
                  ]} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value" label={({name, percent}) => `${name} ${(percent*100).toFixed(0)}%`}>
                    {CHART_COLORS.map((c, i) => <Cell key={i} fill={c} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-bold text-gray-900 mb-4">Signups by Month</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={[
                  { month: 'Jan', signups: Math.round(kpis.new_signups_30d * 0.4) },
                  { month: 'Feb', signups: Math.round(kpis.new_signups_30d * 0.6) },
                  { month: 'Mar', signups: Math.round(kpis.new_signups_30d * 0.8) },
                  { month: 'Apr', signups: kpis.new_signups_30d },
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9CA3AF' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#9CA3AF' }} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #f3f4f6' }} />
                  <Bar dataKey="signups" fill="#F97316" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-bold text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Manage Users', icon: Users, mod: 'users' },
                  { label: 'Send Notification', icon: Bell, mod: 'notifications' },
                  { label: 'Revenue Report', icon: BarChart3, mod: 'revenue' },
                  { label: 'Audit Trail', icon: FileText, mod: 'audit' },
                ].map(a => (
                  <button key={a.mod} onClick={() => setActiveModule(a.mod)}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-orange-50 hover:text-orange-600 transition text-sm font-medium text-gray-700">
                    <a.icon className="w-5 h-5" /> {a.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h3 className="font-bold text-gray-900 mb-4">Platform Stats</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Total Appointments</span><span className="font-bold">{kpis.total_appointments}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">OTP Logins</span><span className="font-bold">{kpis.otp_users}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">MRR</span><span className="font-bold">${kpis.mrr}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">New This Week</span><span className="font-bold text-emerald-600">+{kpis.new_signups_7d}</span></div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderUsers = (roleFilter = '') => (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">{roleFilter ? 'HealthTrack Users' : 'All Users'}</h1>
        <div className="flex items-center gap-2">
          {selectedUsers.length > 0 && (
            <div className="flex items-center gap-2 mr-3 px-3 py-1.5 bg-orange-50 rounded-xl text-sm">
              <span className="font-medium text-orange-700">{selectedUsers.length} selected</span>
              <button onClick={() => handleBulkAction('activate')} className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-200">Activate</button>
              <button onClick={() => handleBulkAction('deactivate')} className="px-2 py-1 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200">Deactivate</button>
              <button onClick={() => handleBulkAction('change_plan', 'basic')} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-200">Upgrade to Basic</button>
            </div>
          )}
          <button onClick={() => { const csv = users.map(u => `${u.name},${u.email},${u.role},${u.phone_number || ''},${u.city || ''}`).join('\n'); const blob = new Blob([`Name,Email,Role,Phone,City\n${csv}`], {type:'text/csv'}); const a=document.createElement('a'); a.href=URL.createObjectURL(blob); a.download='users_export.csv'; a.click(); }}
            className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2">
            <Download className="w-4 h-4" /> Export
          </button>
        </div>
      </div>
      {/* Search & Filters */}
      <div className="flex flex-wrap gap-3 bg-white p-4 rounded-2xl border border-gray-100">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={userSearch} onChange={e => { setUserSearch(e.target.value); setUserPage(1); }}
            placeholder="Search name, email, phone..." className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 outline-none" />
        </div>
        {!roleFilter && (
          <select value={userRoleFilter} onChange={e => { setUserRoleFilter(e.target.value); setUserPage(1); }}
            className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white">
            <option value="">All Roles</option>
            <option value="user">Users</option>
            <option value="doctor">Doctors</option>
            <option value="clinic">Clinics</option>
            <option value="lab">Labs</option>
            <option value="admin">Admins</option>
          </select>
        )}
        <button onClick={fetchUsers} className="px-3 py-2.5 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600 flex items-center gap-2">
          <Filter className="w-4 h-4" /> Apply
        </button>
      </div>
      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="py-3 px-4 w-10"><input type="checkbox" onChange={(e) => e.target.checked ? setSelectedUsers(users.map(u=>u.id)) : setSelectedUsers([])} className="rounded" /></th>
                <th className="py-3 px-4 text-[11px] font-bold text-gray-500 uppercase tracking-wider">User</th>
                <th className="py-3 px-4 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Role</th>
                <th className="py-3 px-4 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Phone</th>
                <th className="py-3 px-4 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Location</th>
                <th className="py-3 px-4 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Plan</th>
                <th className="py-3 px-4 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Last Login</th>
                <th className="py-3 px-4 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={8} className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-orange-500" /></td></tr> :
                users.length === 0 ? <tr><td colSpan={8} className="py-12 text-center text-gray-400">No users found</td></tr> :
                users.map(u => <UserRow key={u.id || u.email} user={u} onView={handleViewUser} onEdit={() => {}} selected={selectedUsers.includes(u.id)} onSelect={toggleSelectUser} />)}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
          <div className="text-sm text-gray-500">Showing {users.length} of {userTotal} users</div>
          <div className="flex items-center gap-2">
            <button onClick={() => setUserPage(p => Math.max(1, p-1))} disabled={userPage <= 1} className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm font-medium">Page {userPage}</span>
            <button onClick={() => setUserPage(p => p+1)} disabled={users.length < 20} className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderUser360 = () => {
    if (!selectedUser360) return null;
    const u = user360Data?.user || selectedUser360;
    const tabs = [
      { id: 'profile', label: 'Profile', icon: UserCircle },
      { id: 'activity', label: 'Activity', icon: Activity },
      { id: 'appointments', label: 'Appointments', icon: Calendar },
      { id: 'audit', label: 'Audit Trail', icon: FileText },
    ];
    return (
      <div className="space-y-4">
        <button onClick={() => setActiveModule('users')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
          <ChevronLeft className="w-4 h-4" /> Back to Users
        </button>
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-400 to-amber-400 flex items-center justify-center text-white font-bold text-2xl shadow-lg">
              {(u.name || 'U')[0].toUpperCase()}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">{u.name || 'Unknown'}</h2>
              <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-500">
                <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{u.email}</span>
                {u.phone_number && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{u.phone_number}</span>}
                {u.city && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{[u.city, u.state, u.country].filter(Boolean).join(', ')}</span>}
              </div>
            </div>
            <span className={`px-3 py-1 text-xs font-bold uppercase rounded-full ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' : u.role === 'doctor' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>{u.role}</span>
          </div>
        </div>
        <div className="flex gap-1 bg-white rounded-2xl border border-gray-100 p-1.5">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setUser360Tab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition ${user360Tab === t.id ? 'bg-orange-500 text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          {user360Tab === 'profile' && (
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { label: 'Full Name', value: u.name },
                { label: 'Email', value: u.email },
                { label: 'Phone', value: u.phone_number || '-' },
                { label: 'Role', value: u.role },
                { label: 'Plan', value: u.plan || 'Free' },
                { label: 'Blood Group', value: u.bloodGroup || '-' },
                { label: 'Country', value: u.country || '-' },
                { label: 'State', value: u.state || '-' },
                { label: 'City', value: u.city || '-' },
                { label: 'Auth Method', value: u.auth_method || 'email' },
                { label: 'Created', value: u.created_at ? new Date(u.created_at).toLocaleString() : '-' },
                { label: 'Last Login', value: u.last_login ? new Date(u.last_login).toLocaleString() : 'Never' },
              ].map(f => (
                <div key={f.label}>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{f.label}</div>
                  <div className="text-sm font-medium text-gray-900">{f.value || '-'}</div>
                </div>
              ))}
            </div>
          )}
          {user360Tab === 'activity' && user360Data && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-50 rounded-xl"><div className="text-2xl font-bold">{user360Data.activity_summary.total_logins}</div><div className="text-xs text-gray-500">Total Logins</div></div>
                <div className="p-4 bg-gray-50 rounded-xl"><div className="text-2xl font-bold">{user360Data.activity_summary.engagement_score}%</div><div className="text-xs text-gray-500">Engagement</div></div>
                <div className="p-4 bg-gray-50 rounded-xl"><div className="text-2xl font-bold">{user360Data.health_records_count}</div><div className="text-xs text-gray-500">Health Records</div></div>
                <div className="p-4 bg-gray-50 rounded-xl"><div className="text-2xl font-bold">{user360Data.appointments.length}</div><div className="text-xs text-gray-500">Appointments</div></div>
              </div>
              <h4 className="font-semibold text-gray-700">Features Used</h4>
              <div className="flex flex-wrap gap-2">
                {user360Data.activity_summary.features_used.map(f => <span key={f} className="px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium">{f}</span>)}
              </div>
            </div>
          )}
          {user360Tab === 'appointments' && user360Data && (
            <div className="space-y-3">
              {user360Data.appointments.length === 0 ? <p className="text-gray-400 text-sm">No appointments found</p> :
                user360Data.appointments.map((a, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div>
                      <div className="font-medium text-sm">{a.specialization || 'General'}</div>
                      <div className="text-xs text-gray-500">{a.appointment_date ? new Date(a.appointment_date).toLocaleString() : '-'}</div>
                    </div>
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${a.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{a.status}</span>
                  </div>
                ))}
            </div>
          )}
          {user360Tab === 'audit' && user360Data && (
            <div className="space-y-2">
              {(user360Data.audit_trail || []).length === 0 ? <p className="text-gray-400 text-sm">No audit events</p> :
                user360Data.audit_trail.map((a, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl text-sm">
                    <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div className="flex-1"><span className="font-medium">{a.action}</span> <span className="text-gray-500">{a.details}</span></div>
                    <span className="text-xs text-gray-400">{a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}</span>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderRevenue = () => {
    if (!revenue) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;
    const monthly = revenue.monthly_revenue || [];
    const planEntries = Object.entries(revenue.revenue_by_plan || {});
    const geo = revenue.users_by_geography || {};
    const cur = revenue.revenue_by_currency || {};

    return (
      <div className="space-y-6" data-testid="revenue-module">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><DollarSign className="w-6 h-6 text-emerald-500" /> Revenue Analytics</h1>
            <p className="text-sm text-gray-500 mt-1">Last {revenue.range_days} days · {revenue.active_subscriptions} active subscriptions · {revenue.cancelled_subscriptions} cancelled</p>
          </div>
          <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 p-1" data-testid="revenue-range-selector">
            {[30, 90, 180, 365].map(d => (
              <button key={d} onClick={() => setRevenueRange(d)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${revenueRange === d ? 'bg-orange-500 text-white shadow' : 'text-gray-600 hover:bg-gray-50'}`}>
                {d}d
              </button>
            ))}
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard label="Total Revenue" value={`$${revenue.total_revenue.toLocaleString()}`} icon={DollarSign} color="bg-gradient-to-br from-green-500 to-emerald-500" trend={22} />
          <KpiCard label="MRR" value={`$${revenue.mrr.toLocaleString()}`} icon={TrendingUp} color="bg-gradient-to-br from-blue-500 to-cyan-500" />
          <KpiCard label="ARR" value={`$${revenue.arr.toLocaleString()}`} icon={BarChart3} color="bg-gradient-to-br from-purple-500 to-violet-500" />
          <KpiCard label="ARPU" value={`$${revenue.arpu}`} icon={UserCircle} color="bg-gradient-to-br from-amber-500 to-yellow-500" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard label="LTV" value={`$${revenue.ltv}`} icon={Globe} color="bg-blue-500" />
          <KpiCard label="Churn Rate" value={`${revenue.churn_rate}%`} icon={TrendingDown} color={revenue.churn_rate > 5 ? "bg-red-500" : "bg-emerald-500"} trend={-0.5} />
          <KpiCard label="Active Subs" value={revenue.active_subscriptions} icon={CreditCard} color="bg-emerald-500" />
          <KpiCard label="Cancelled" value={revenue.cancelled_subscriptions} icon={XCircle} color="bg-rose-500" />
        </div>

        {/* Monthly trend */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="monthly-revenue-chart">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900">Monthly Revenue Trend</h3>
            <span className="text-xs text-gray-400">{monthly.length} months</span>
          </div>
          {monthly.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              No revenue transactions in the last {revenue.range_days} days. Once Stripe / Razorpay payments flow in, this chart populates automatically.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={monthly} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF9A3B" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#FF9A3B" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" stroke="#9ca3af" fontSize={11} />
                <YAxis stroke="#9ca3af" fontSize={11} tickFormatter={v => `$${v}`} />
                <Tooltip formatter={(v, n) => n === 'revenue' ? [`$${Number(v).toLocaleString()}`, 'Revenue'] : [v, 'Transactions']} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 24px rgba(0,0,0,0.1)' }} />
                <Area type="monotone" dataKey="revenue" stroke="#E55A00" strokeWidth={2.5} fill="url(#revFill)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Plan + Geography + Currency */}
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><CreditCard className="w-4 h-4 text-orange-500" /> By Plan</h3>
            {planEntries.every(([, v]) => v === 0) ? <div className="text-xs text-gray-400 py-6 text-center">No paid plans yet</div> : (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={planEntries.map(([name, value]) => ({ name, value: Number(value) }))} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3} dataKey="value" label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {CHART_COLORS.map((c, i) => <Cell key={i} fill={c} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><Globe className="w-4 h-4 text-blue-500" /> By Geography</h3>
            <div className="space-y-2.5 max-h-[200px] overflow-y-auto pr-2">
              {Object.entries(geo).slice(0, 8).map(([country, users]) => {
                const max = Math.max(...Object.values(geo), 1);
                return (
                  <div key={country} className="flex items-center gap-3">
                    <span className="text-xs font-medium text-gray-700 w-24 truncate">{country}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-2"><div className="h-2 rounded-full bg-gradient-to-r from-blue-400 to-blue-600" style={{ width: `${(users / max) * 100}%` }} /></div>
                    <span className="text-xs font-bold text-gray-900 w-10 text-right tabular-nums">{users}</span>
                  </div>
                );
              })}
              {Object.keys(geo).length === 0 && <div className="text-xs text-gray-400 py-6 text-center">No geo data yet</div>}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><DollarSign className="w-4 h-4 text-emerald-500" /> By Currency</h3>
            <div className="space-y-2.5 max-h-[200px] overflow-y-auto pr-2">
              {Object.entries(cur).map(([code, amt]) => (
                <div key={code} className="flex items-center justify-between p-2 bg-gradient-to-r from-emerald-50 to-transparent rounded-lg">
                  <span className="text-xs font-bold text-gray-700">{code}</span>
                  <span className="text-sm font-bold text-emerald-700 tabular-nums">{Number(amt).toLocaleString()}</span>
                </div>
              ))}
              {Object.keys(cur).length === 0 && <div className="text-xs text-gray-400 py-6 text-center">No paid txns yet</div>}
            </div>
          </div>
        </div>

        {/* Recent transactions */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-bold text-gray-900">Recent Transactions</h3>
            <span className="text-xs text-gray-400">{(revenue.recent_transactions || []).length} most recent</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                <tr>
                  <th className="text-left py-3 px-4">When</th>
                  <th className="text-left py-3 px-4">User</th>
                  <th className="text-left py-3 px-4">Amount</th>
                  <th className="text-left py-3 px-4">Plan</th>
                  <th className="text-left py-3 px-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {(revenue.recent_transactions || []).slice(0, 12).map((t, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-orange-50/30">
                    <td className="py-2.5 px-4 text-xs text-gray-500">{t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}</td>
                    <td className="py-2.5 px-4 text-sm text-gray-900 font-medium">{t.user_email || t.user_id || '—'}</td>
                    <td className="py-2.5 px-4 text-sm font-bold text-emerald-700 tabular-nums">${Number(t.amount || 0).toLocaleString()} <span className="text-[10px] font-normal text-gray-500">{t.currency || 'USD'}</span></td>
                    <td className="py-2.5 px-4 text-xs text-gray-700 capitalize">{t.plan_id || '—'}</td>
                    <td className="py-2.5 px-4"><span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${t.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : t.payment_status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{t.payment_status || 'pending'}</span></td>
                  </tr>
                ))}
                {(!revenue.recent_transactions || revenue.recent_transactions.length === 0) && (
                  <tr><td colSpan={5} className="py-8 text-center text-gray-400 text-sm">No transactions yet — Stripe / Razorpay integrations pending</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderAuditLogs = () => {
    const ACTION_COLORS = {
      admin_login: 'bg-blue-100 text-blue-700',
      internal_admin_login: 'bg-purple-100 text-purple-700',
      tenant_admin_login: 'bg-emerald-100 text-emerald-700',
      internal_admin_created: 'bg-purple-100 text-purple-700',
      internal_admin_updated: 'bg-amber-100 text-amber-700',
      internal_admin_deleted: 'bg-red-100 text-red-700',
      internal_admin_password_reset: 'bg-blue-100 text-blue-700',
      tenant_member_created: 'bg-emerald-100 text-emerald-700',
      tenant_member_updated: 'bg-amber-100 text-amber-700',
      tenant_member_removed: 'bg-red-100 text-red-700',
      tenant_member_password_reset: 'bg-blue-100 text-blue-700',
    };
    const colorFor = (a) => ACTION_COLORS[a] || 'bg-gray-100 text-gray-700';

    return (
      <div className="space-y-5" data-testid="audit-module">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><FileText className="w-6 h-6 text-blue-500" /> Audit Logs</h1>
            <p className="text-sm text-gray-500 mt-1">{auditTotal.toLocaleString()} total entries · immutable record of every admin action</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setAuditFilters({ action: '', admin_email: '', start_date: '', end_date: '' })}
              className="px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl flex items-center gap-1.5"
              data-testid="audit-clear-filters">
              <X className="w-3.5 h-3.5" /> Clear
            </button>
            <button onClick={exportAuditLogs}
              className="px-4 py-2 text-xs font-semibold text-white bg-gradient-to-r from-blue-500 to-cyan-500 hover:shadow-lg rounded-xl flex items-center gap-1.5"
              data-testid="audit-export-csv">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4" data-testid="audit-filters">
          <div className="grid md:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Action</label>
              <input type="text" value={auditFilters.action} onChange={e => setAuditFilters({ ...auditFilters, action: e.target.value })}
                placeholder="e.g. login, password_reset"
                data-testid="audit-filter-action"
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-300 outline-none" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Admin Email</label>
              <input type="text" value={auditFilters.admin_email} onChange={e => setAuditFilters({ ...auditFilters, admin_email: e.target.value })}
                placeholder="filter by email"
                data-testid="audit-filter-email"
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-300 outline-none" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">From</label>
              <input type="date" value={auditFilters.start_date} onChange={e => setAuditFilters({ ...auditFilters, start_date: e.target.value })}
                data-testid="audit-filter-from"
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-300 outline-none" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">To</label>
              <input type="date" value={auditFilters.end_date} onChange={e => setAuditFilters({ ...auditFilters, end_date: e.target.value })}
                data-testid="audit-filter-to"
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-300 outline-none" />
            </div>
          </div>
        </div>

        {/* Action breakdown chips */}
        {Object.keys(auditBreakdown).length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="audit-breakdown">
            {Object.entries(auditBreakdown).map(([a, c]) => (
              <button key={a} onClick={() => setAuditFilters({ ...auditFilters, action: a })}
                className={`px-3 py-1.5 rounded-full text-[11px] font-bold ${colorFor(a)} hover:scale-105 transition`}>
                {a} <span className="opacity-70">· {c}</span>
              </button>
            ))}
          </div>
        )}

        {/* Logs Table */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                <tr>
                  <th className="text-left py-3 px-4">Action</th>
                  <th className="text-left py-3 px-4">Details</th>
                  <th className="text-left py-3 px-4">Admin</th>
                  <th className="text-left py-3 px-4">Timestamp</th>
                </tr>
              </thead>
              <tbody data-testid="audit-logs-table">
                {auditLogs.length === 0 ? (
                  <tr><td colSpan={4} className="py-12 text-center text-gray-400 text-sm">No audit logs match these filters</td></tr>
                ) : auditLogs.map((l, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-blue-50/30 transition">
                    <td className="py-3 px-4"><span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold ${colorFor(l.action)}`}>{l.action}</span></td>
                    <td className="py-3 px-4 text-sm text-gray-700 max-w-md">{l.details}</td>
                    <td className="py-3 px-4 text-xs font-medium text-gray-600">{l.admin_email || '—'}</td>
                    <td className="py-3 px-4 text-xs text-gray-500 whitespace-nowrap">{l.timestamp ? new Date(l.timestamp).toLocaleString() : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderNotifications = () => (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
        <h3 className="font-bold text-gray-900">Send Notification</h3>
        <input value={notifForm.title} onChange={e => setNotifForm({...notifForm, title: e.target.value})} placeholder="Notification title"
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 outline-none" />
        <textarea value={notifForm.message} onChange={e => setNotifForm({...notifForm, message: e.target.value})} placeholder="Message body..." rows={3}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 outline-none" />
        <div className="flex gap-3">
          <select value={notifForm.target_segment} onChange={e => setNotifForm({...notifForm, target_segment: e.target.value})}
            className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white">
            <option value="all">All Users</option>
            <option value="users">Users Only</option>
            <option value="doctors">Doctors Only</option>
            <option value="paid">Paid Users</option>
            <option value="free">Free Users</option>
          </select>
          <button onClick={handleSendNotification} disabled={!notifForm.title || !notifForm.message}
            className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-medium text-sm hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 flex items-center gap-2">
            <Send className="w-4 h-4" /> Send
          </button>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h3 className="font-bold text-gray-900 mb-4">Sent Notifications</h3>
        <div className="space-y-3">
          {notifications.length === 0 ? <p className="text-gray-400 text-sm">No notifications sent yet</p> :
            notifications.map((n, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div>
                  <div className="font-medium text-sm">{n.title}</div>
                  <div className="text-xs text-gray-500">{n.target_segment} - {n.target_count} recipients</div>
                </div>
                <div className="text-right">
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-bold">{n.status}</span>
                  <div className="text-[10px] text-gray-400 mt-1">{n.sent_at ? new Date(n.sent_at).toLocaleString() : ''}</div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );

  const renderPlans = () => (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Plans & Pricing</h1>
      <div className="grid md:grid-cols-4 gap-4">
        {[
          { name: 'Free', price: '0', color: 'from-gray-400 to-gray-500', features: ['Basic health tracking', '1 AI analysis/month'] },
          { name: 'Basic', price: '299', color: 'from-blue-500 to-cyan-500', features: ['Unlimited AI analysis', 'Doctor consultations', 'Lab reports'] },
          { name: 'Pro', price: '999', color: 'from-orange-500 to-amber-500', features: ['Everything in Basic', 'Wearable sync', 'Priority support', 'Family sharing'] },
          { name: 'Enterprise', price: '4,999', color: 'from-purple-500 to-violet-500', features: ['Everything in Pro', 'Custom integrations', 'Dedicated support', 'SLA'] },
        ].map(p => (
          <div key={p.name} className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-lg transition">
            <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${p.color} flex items-center justify-center text-white font-bold text-lg mb-4`}>{p.name[0]}</div>
            <h3 className="text-lg font-bold text-gray-900">{p.name}</h3>
            <div className="text-2xl font-bold text-gray-900 mt-2">INR {p.price}<span className="text-sm font-normal text-gray-500">/mo</span></div>
            <ul className="mt-4 space-y-2">
              {p.features.map(f => <li key={f} className="flex items-center gap-2 text-sm text-gray-600"><CheckCircle className="w-4 h-4 text-emerald-500" />{f}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );

  const renderRBAC = () => (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Roles & Access Control</h1>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          { role: 'Super Admin', color: 'from-purple-500 to-violet-500', perms: ['Full platform access', 'User management', 'Revenue & billing', 'RBAC management', 'Audit logs', 'System config'] },
          { role: 'Support Admin', color: 'from-blue-500 to-cyan-500', perms: ['View users', 'Support tickets', 'User notes', 'Activity logs', 'Send notifications'] },
          { role: 'Finance Admin', color: 'from-green-500 to-emerald-500', perms: ['Revenue analytics', 'Billing records', 'Refund processing', 'Plan management', 'Export reports'] },
          { role: 'Marketing Admin', color: 'from-orange-500 to-amber-500', perms: ['User segments', 'Send campaigns', 'Push notifications', 'Cohort analysis', 'Engagement metrics'] },
          { role: 'Compliance Admin', color: 'from-red-500 to-rose-500', perms: ['Audit logs (read)', 'Data access monitor', 'Security events', 'Policy enforcement', 'Export compliance'] },
        ].map(r => (
          <div key={r.role} className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-lg transition">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${r.color} flex items-center justify-center text-white mb-3`}><Lock className="w-5 h-5" /></div>
            <h3 className="font-bold text-gray-900">{r.role}</h3>
            <ul className="mt-3 space-y-1.5">
              {r.perms.map(p => <li key={p} className="flex items-center gap-2 text-sm text-gray-600"><CheckCircle className="w-3.5 h-3.5 text-emerald-500" />{p}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );

  // ==================== INTERNAL ADMINS (Super Admin only) ====================
  const FUNCTION_META = {
    super_admin:     { label: 'Super Admin',      color: 'from-purple-500 to-violet-500', bg: 'bg-purple-50 text-purple-700',   desc: 'Full platform access across all products and tenants' },
    support_admin:   { label: 'Support Admin',    color: 'from-blue-500 to-cyan-500',    bg: 'bg-blue-50 text-blue-700',       desc: 'User queries, tickets, notifications' },
    finance_admin:   { label: 'Finance Admin',    color: 'from-emerald-500 to-green-500', bg: 'bg-emerald-50 text-emerald-700', desc: 'Revenue, billing, refunds, plans' },
    marketing_admin: { label: 'Marketing Admin',  color: 'from-orange-500 to-amber-500', bg: 'bg-orange-50 text-orange-700',   desc: 'Segments, campaigns, push, cohorts' },
    compliance_admin:{ label: 'Compliance Admin', color: 'from-red-500 to-rose-500',     bg: 'bg-red-50 text-red-700',         desc: 'Audit logs, security, policies' },
    ops_admin:       { label: 'Ops Admin',        color: 'from-slate-500 to-gray-600',   bg: 'bg-slate-50 text-slate-700',     desc: 'System health, activity, monitoring' },
  };

  const renderInternalAdmins = () => (
    <div className="space-y-6" data-testid="internal-admins-module">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-purple-500" /> Internal Admins
          </h1>
          <p className="text-sm text-gray-500 mt-1">Create, manage, and disable function-based admin access across Infuse.</p>
        </div>
        <button onClick={() => { setNewAdminCredentials(null); setShowCreateAdmin(true); }}
          className="px-4 py-2 bg-gradient-to-r from-purple-500 to-violet-500 text-white font-semibold rounded-xl text-sm hover:shadow-lg transition flex items-center gap-2"
          data-testid="create-internal-admin-btn">
          <UserCheck className="w-4 h-4" /> Invite Admin
        </button>
      </div>

      {/* Role function legend */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {Object.entries(FUNCTION_META).map(([key, m]) => (
          <div key={key} className="bg-white rounded-xl border border-gray-100 p-3">
            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${m.color} flex items-center justify-center mb-2`}>
              <Lock className="w-4 h-4 text-white" />
            </div>
            <div className="text-xs font-bold text-gray-900">{m.label}</div>
            <div className="text-[10px] text-gray-500 mt-0.5 line-clamp-2">{m.desc}</div>
          </div>
        ))}
      </div>

      {/* Admins table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
              <tr>
                <th className="text-left py-3 px-4">Admin</th>
                <th className="text-left py-3 px-4">Function</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-left py-3 px-4">Created</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="internal-admins-table">
              {internalAdmins.map(a => {
                const meta = FUNCTION_META[a.admin_function] || FUNCTION_META.support_admin;
                return (
                  <tr key={a.id} className="border-b border-gray-50 hover:bg-purple-50/30 transition">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${meta.color} flex items-center justify-center text-white font-bold text-xs`}>
                          {(a.name || a.email)[0]?.toUpperCase()}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 text-sm flex items-center gap-1.5">
                            {a.name || a.email}
                            {a.is_root && <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 rounded">ROOT</span>}
                          </div>
                          <div className="text-xs text-gray-500">{a.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {a.is_root ? (
                        <span className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full ${meta.bg}`}>{meta.label}</span>
                      ) : (
                        <select
                          value={a.admin_function}
                          onChange={e => handleUpdateInternalAdmin(a.id, { admin_function: e.target.value })}
                          className="text-[11px] font-semibold px-2 py-1 rounded-lg border border-gray-200 bg-white focus:ring-2 focus:ring-purple-300"
                          data-testid={`change-function-${a.id}`}
                        >
                          {Object.entries(FUNCTION_META).filter(([k]) => k !== 'super_admin').map(([k, m]) => (
                            <option key={k} value={k}>{m.label}</option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${a.status === 'disabled' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {a.status || 'active'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs text-gray-500">{a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}</td>
                    <td className="py-3 px-4">
                      {!a.is_root && (
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleResetInternalAdminPwd(a.id)} className="p-1.5 hover:bg-blue-100 rounded-lg text-blue-600" title="Reset password" data-testid={`reset-pwd-${a.id}`}>
                            <RefreshCw className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleUpdateInternalAdmin(a.id, { status: a.status === 'disabled' ? 'active' : 'disabled' })} className="p-1.5 hover:bg-amber-100 rounded-lg text-amber-600" title={a.status === 'disabled' ? 'Enable' : 'Disable'}>
                            {a.status === 'disabled' ? <CheckCircle className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                          </button>
                          <button onClick={() => handleDeleteInternalAdmin(a.id, a.email)} className="p-1.5 hover:bg-red-100 rounded-lg text-red-600" title="Delete" data-testid={`delete-${a.id}`}>
                            <XCircle className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {internalAdmins.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-gray-400 text-sm">No internal admins yet. Invite one to get started.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Admin Modal */}
      {showCreateAdmin && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowCreateAdmin(false)}>
          <div className="w-full max-w-lg bg-white rounded-2xl p-6 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="create-admin-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Invite New Admin</h3>
            <p className="text-xs text-gray-500 mb-4">The admin will receive an invitation code to set their own password.</p>
            {newAdminCredentials ? (
              <div className="space-y-3">
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <div className="flex items-center gap-2 font-bold text-emerald-700 text-sm"><CheckCircle className="w-4 h-4" /> Admin created successfully!</div>
                  <div className="mt-3 space-y-2 text-xs">
                    <div><span className="text-gray-500">Email:</span> <span className="font-mono font-bold">{newAdminCredentials.email}</span></div>
                    <div><span className="text-gray-500">Function:</span> <span className="font-semibold">{FUNCTION_META[newAdminCredentials.admin_function]?.label}</span></div>
                    {newAdminCredentials.temporary_password && (
                      <div className="p-2 bg-white rounded-lg border border-dashed border-amber-300">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-amber-600">Temporary Password (share securely)</div>
                        <div className="font-mono text-sm text-gray-900 select-all">{newAdminCredentials.temporary_password}</div>
                      </div>
                    )}
                    <div className="p-2 bg-white rounded-lg border border-dashed border-blue-300">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-blue-600">Self-Register Link</div>
                      <div className="font-mono text-xs text-gray-700 break-all select-all">{window.location.origin}{newAdminCredentials.invite_url}</div>
                    </div>
                  </div>
                </div>
                <button onClick={() => { setShowCreateAdmin(false); setNewAdminCredentials(null); }}
                  className="w-full py-2.5 bg-gray-900 text-white rounded-xl font-semibold text-sm">Done</button>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-gray-700">Email</label>
                  <input type="email" value={adminForm.email} onChange={e => setAdminForm({ ...adminForm, email: e.target.value })}
                    placeholder="admin@infuse.net.in" data-testid="admin-form-email"
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-300" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">Full Name</label>
                  <input type="text" value={adminForm.name} onChange={e => setAdminForm({ ...adminForm, name: e.target.value })}
                    placeholder="Priya Sharma" data-testid="admin-form-name"
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-300" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">Admin Function</label>
                  <select value={adminForm.admin_function} onChange={e => setAdminForm({ ...adminForm, admin_function: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white" data-testid="admin-form-function">
                    {Object.entries(FUNCTION_META).filter(([k]) => k !== 'super_admin').map(([k, m]) => (
                      <option key={k} value={k}>{m.label} — {m.desc}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2 mt-5">
                  <button onClick={() => setShowCreateAdmin(false)} className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold text-sm">Cancel</button>
                  <button onClick={handleCreateInternalAdmin} disabled={!adminForm.email || !adminForm.name}
                    data-testid="submit-create-admin"
                    className="flex-1 py-2.5 bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-xl font-semibold text-sm disabled:opacity-50">
                    Create Admin
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // ==================== TEAM MANAGEMENT (Tenant Admins) ====================
  const TENANT_ROLES = {
    clinic: ['doctor', 'nurse', 'staff', 'receptionist', 'patient'],
    hospital: ['doctor', 'nurse', 'staff', 'receptionist', 'patient'],
    lab: ['lab_technician', 'phlebotomist', 'staff', 'patient'],
  };
  const currentRoleList = TENANT_ROLES[adminInfo?.tenant_type] || ['staff'];

  const renderTeam = () => (
    <div className="space-y-6" data-testid="team-module">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <UserCheck className="w-6 h-6 text-emerald-500" /> My Team
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage doctors, staff, and patients inside <span className="font-semibold text-gray-700">{adminInfo?.name}</span> — data is strictly scoped to your organization.</p>
        </div>
        <button onClick={() => { setNewMemberCredentials(null); setShowAddMember(true); }}
          className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold rounded-xl text-sm hover:shadow-lg transition flex items-center gap-2"
          data-testid="add-team-member-btn">
          <UserCheck className="w-4 h-4" /> Add Member
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {currentRoleList.map(r => {
          const count = teamMembers.filter(m => m.role === r).length;
          return (
            <div key={r} className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="text-2xl font-bold text-gray-900 tabular-nums">{count}</div>
              <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{r.replace('_', ' ')}s</div>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
              <tr>
                <th className="text-left py-3 px-4">Member</th>
                <th className="text-left py-3 px-4">Role</th>
                <th className="text-left py-3 px-4">Phone</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="team-members-table">
              {teamMembers.map(m => (
                <tr key={m.id} className="border-b border-gray-50 hover:bg-emerald-50/30 transition">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-teal-400 flex items-center justify-center text-white font-bold text-xs">
                        {(m.name || m.email)[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 text-sm">{m.name || m.email}</div>
                        <div className="text-xs text-gray-500">{m.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <select value={m.role} onChange={e => handleUpdateTeamMember(m.id, { role: e.target.value })}
                      className="text-[11px] font-semibold px-2 py-1 rounded-lg border border-gray-200 bg-white focus:ring-2 focus:ring-emerald-300">
                      {currentRoleList.map(r => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                    </select>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600">{m.phone_number || m.phone || '—'}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${m.status === 'disabled' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {m.status || 'active'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleResetTeamMemberPwd(m.id)} className="p-1.5 hover:bg-blue-100 rounded-lg text-blue-600" title="Reset password"><RefreshCw className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleUpdateTeamMember(m.id, { status: m.status === 'disabled' ? 'active' : 'disabled' })} className="p-1.5 hover:bg-amber-100 rounded-lg text-amber-600" title={m.status === 'disabled' ? 'Enable' : 'Disable'}>
                        {m.status === 'disabled' ? <CheckCircle className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => handleRemoveTeamMember(m.id, m.email)} className="p-1.5 hover:bg-red-100 rounded-lg text-red-600" title="Remove from org"><XCircle className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {teamMembers.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-gray-400 text-sm">No team members yet. Add your first member to get started.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddMember && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowAddMember(false)}>
          <div className="w-full max-w-lg bg-white rounded-2xl p-6 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="add-member-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Add Team Member</h3>
            <p className="text-xs text-gray-500 mb-4">New members are added directly to your organization with per-user credentials.</p>
            {newMemberCredentials ? (
              <div className="space-y-3">
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <div className="flex items-center gap-2 font-bold text-emerald-700 text-sm"><CheckCircle className="w-4 h-4" /> Member added!</div>
                  <div className="mt-3 space-y-2 text-xs">
                    <div><span className="text-gray-500">Email:</span> <span className="font-mono font-bold">{newMemberCredentials.email}</span></div>
                    <div><span className="text-gray-500">Role:</span> <span className="font-semibold">{newMemberCredentials.role}</span></div>
                    {newMemberCredentials.temporary_password && (
                      <div className="p-2 bg-white rounded-lg border border-dashed border-amber-300">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-amber-600">Temporary Password</div>
                        <div className="font-mono text-sm text-gray-900 select-all">{newMemberCredentials.temporary_password}</div>
                        <div className="text-[10px] text-gray-400 mt-1">Share securely. Member should change on first login.</div>
                      </div>
                    )}
                    {newMemberCredentials.linked && (
                      <div className="text-xs text-blue-600">This email already had an account — it was linked to your org.</div>
                    )}
                  </div>
                </div>
                <button onClick={() => { setShowAddMember(false); setNewMemberCredentials(null); }}
                  className="w-full py-2.5 bg-gray-900 text-white rounded-xl font-semibold text-sm">Done</button>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-gray-700">Email</label>
                  <input type="email" value={memberForm.email} onChange={e => setMemberForm({ ...memberForm, email: e.target.value })}
                    placeholder="doctor@clinic.com" data-testid="member-form-email"
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-300" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">Full Name</label>
                  <input type="text" value={memberForm.name} onChange={e => setMemberForm({ ...memberForm, name: e.target.value })}
                    placeholder="Dr. Priya Sharma" data-testid="member-form-name"
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-300" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-700">Role</label>
                    <select value={memberForm.role} onChange={e => setMemberForm({ ...memberForm, role: e.target.value })}
                      className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white" data-testid="member-form-role">
                      {currentRoleList.map(r => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-700">Phone (optional)</label>
                    <input type="tel" value={memberForm.phone} onChange={e => setMemberForm({ ...memberForm, phone: e.target.value })}
                      placeholder="+91 98765 43210"
                      className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm" />
                  </div>
                </div>
                <div className="flex gap-2 mt-5">
                  <button onClick={() => setShowAddMember(false)} className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold text-sm">Cancel</button>
                  <button onClick={handleAddTeamMember} disabled={!memberForm.email || !memberForm.name}
                    data-testid="submit-add-member"
                    className="flex-1 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-semibold text-sm disabled:opacity-50">
                    Add Member
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    switch (activeModule) {
      case 'dashboard': return renderDashboard();
      case 'users': return renderUsers();
      case 'healthtrack-users': return renderUsers('user');
      case 'user360': return renderUser360();
      case 'revenue': return renderRevenue();
      case 'audit': return renderAuditLogs();
      case 'notifications': return renderNotifications();
      case 'plans': return renderPlans();
      case 'rbac': return renderRBAC();
      case 'internal-admins': return renderInternalAdmins();
      case 'team': return renderTeam();
      default: return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="platform-admin">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      
      {/* Sidebar */}
      <aside className={`w-64 bg-gradient-to-b from-gray-900 to-gray-800 min-h-screen flex flex-col fixed left-0 top-0 z-50 transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-white font-bold text-lg">Infuse Admin</div>
              <div className="text-gray-400 text-[10px] uppercase tracking-widest">Platform Portal</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {MODULES.map(m => (
            <button key={m.id} onClick={() => { setActiveModule(m.id); setSidebarOpen(false); if (m.id === 'healthtrack-users') setUserRoleFilter('user'); else if (m.id === 'users') setUserRoleFilter(''); }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                activeModule === m.id ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg' : 'text-gray-400 hover:bg-white/5 hover:text-white'
              }`} data-testid={`nav-${m.id}`}>
              <m.icon className="w-5 h-5" /> {m.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          {adminInfo?.tenant_type && adminInfo.tenant_type !== 'infuse' && (
            <div className="mb-3 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-xl">
              <div className="text-[10px] font-bold text-amber-400 uppercase tracking-widest">{adminInfo.tenant_type} Admin</div>
              <div className="text-xs text-amber-200 mt-0.5 truncate">{adminInfo.name}</div>
              <div className="text-[10px] text-amber-400/60 mt-0.5">Org-scoped data only</div>
            </div>
          )}
          <div className="text-xs text-gray-500 mb-2">Logged in as</div>
          <div className="text-sm text-white font-medium truncate">{adminInfo?.email}</div>
          <button onClick={handleLogout} className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-white/5 text-gray-400 rounded-xl hover:bg-red-500/10 hover:text-red-400 text-sm transition">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>
      {/* Main Content */}
      <main className="flex-1 lg:ml-64 p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
        {/* Mobile header */}
        <div className="lg:hidden flex items-center gap-3 mb-4">
          <button onClick={() => setSidebarOpen(true)} className="p-2 bg-white border border-gray-200 rounded-xl">
            <Menu className="w-5 h-5 text-gray-700" />
          </button>
          <span className="font-bold text-gray-900">Infuse Admin</span>
        </div>
        {renderContent()}
      </main>
    </div>
  );
};

export default InfuseAdminDashboard;
